import axios from 'axios';
import { getCredentials } from '../_redux'; // The selector is fine to import

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

// Typed structurally to avoid importing AppStore/AppDispatch and re-entering
// the authentication_slice circular dependency chain.
// getState typed as any to avoid importing RootState and re-entering the circular dep chain
interface MinimalStore {
  getState: () => any;
  dispatch: (action: unknown) => unknown;
}

export const setupAxiosInterceptors = (
  store: MinimalStore,
  { logout }: { logout: (message?: string) => unknown }
): void => {
  axiosInstance.interceptors.request.use(
    (config) => {
      const state = store.getState();
      const credentials = getCredentials(state);

      if (credentials?.email) {
        if (!config.headers['uid']) {
          config.headers['uid'] = credentials.email;
        }
        if (!config.headers['access-token']) {
          config.headers['access-token'] = credentials.accessToken;
        }
        if (!config.headers['client']) {
          config.headers['client'] = credentials.client;
        }
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Endpoints that legitimately 401 for reasons other than a dead session:
  // a wrong password on sign-in, an expired password-reset link. Logging out on
  // these would wipe the error the form is trying to show.
  const UNAUTHENTICATED_ENDPOINTS = ['/auth/sign_in', '/auth/password', '/auth/validate_token'];
  const isUnauthenticatedEndpoint = (url?: string) =>
    UNAUTHENTICATED_ENDPOINTS.some((path) => (url ?? '').includes(path));

  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error.response?.status;
      const errorData = error.response?.data;
      // Devise's messages are kept as a secondary signal, in case the API ever
      // returns them with a status other than 401.
      const hasSessionErrorMessage =
        Array.isArray(errorData?.errors) &&
        (errorData.errors.includes("Authorized users only.") ||
          errorData.errors.includes("You need to sign in or sign up before continuing."));
      const hasSession = Boolean(getCredentials(store.getState())?.email);

      if (
        hasSession &&
        !isUnauthenticatedEndpoint(error.config?.url) &&
        (status === 401 || hasSessionErrorMessage)
      ) {
        console.warn("Authentication error detected by Axios interceptor. Logging out.");
        store.dispatch(logout("Your session has timed out, please log back in."));
      }

      return Promise.reject(error);
    }
  );
};

export default axiosInstance;
